import React, { useEffect, useRef, useCallback } from 'react';

interface TiledVideoBackgroundProps {
    src: string;
    /** Tile width as a percentage of the viewport width (e.g. 33 = 33vw) */
    resolutionVw: number;
    style?: React.CSSProperties;
    className?: string;
}

/**
 * Tiled video background with seamless looping.
 *
 * Architecture:
 *  - Two hidden <video> elements (A and B) with the same src.
 *  - One canvas that tiles frames via requestAnimationFrame.
 *  - When the active video is CROSSFADE_AHEAD seconds from its end,
 *    the standby video starts playing from t=0.
 *  - When the active video fires 'ended', the canvas switches to the
 *    standby video (already playing) — zero gap.
 *  - The old active video is reset and becomes the new standby.
 *
 * This avoids the seek-and-rebuffer gap that the `loop` attribute causes.
 */

/** How many seconds before the end to pre-roll the standby video. */
const CROSSFADE_AHEAD = 0.3;

const TiledVideoBackground: React.FC<TiledVideoBackgroundProps> = ({
    src,
    resolutionVw,
    style,
    className,
}) => {
    const canvasRef    = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const rafRef       = useRef<number | null>(null);

    // Double buffer
    const videoA   = useRef<HTMLVideoElement>(null);
    const videoB   = useRef<HTMLVideoElement>(null);
    // Which buffer is currently being drawn to canvas
    const activeRef = useRef<'a' | 'b'>('a');

    // Keep resolutionVw accessible inside the RAF loop without re-creating draw
    const resolutionVwRef = useRef(resolutionVw);
    useEffect(() => { resolutionVwRef.current = resolutionVw; }, [resolutionVw]);

    const getVideos = useCallback(() => ({
        active:  activeRef.current === 'a' ? videoA.current : videoB.current,
        standby: activeRef.current === 'a' ? videoB.current : videoA.current,
    }), []);

    const draw = useCallback(() => {
        const canvas    = canvasRef.current;
        const container = containerRef.current;
        const { active } = getVideos();

        if (!canvas || !container || !active || active.readyState < 2) {
            rafRef.current = requestAnimationFrame(draw);
            return;
        }

        // Resize canvas to container if needed
        const cw = container.clientWidth;
        const ch = container.clientHeight;
        if (canvas.width !== cw || canvas.height !== ch) {
            canvas.width  = cw;
            canvas.height = ch;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) { rafRef.current = requestAnimationFrame(draw); return; }

        // Tile dimensions — use the video's real aspect ratio
        const vw     = window.innerWidth;
        const tileW  = Math.round((resolutionVwRef.current / 100) * vw);
        const aspect = active.videoWidth && active.videoHeight
            ? active.videoHeight / active.videoWidth
            : 9 / 16;
        const tileH = Math.round(tileW * aspect);

        if (tileW > 0 && tileH > 0) {
            ctx.clearRect(0, 0, cw, ch);
            for (let y = 0; y < ch; y += tileH) {
                for (let x = 0; x < cw; x += tileW) {
                    ctx.drawImage(active, x, y, tileW, tileH);
                }
            }
        }

        // Pre-roll standby when active is close to its end
        const { standby } = getVideos();
        if (
            standby &&
            active.duration > 0 &&
            active.currentTime >= active.duration - CROSSFADE_AHEAD &&
            standby.paused
        ) {
            standby.currentTime = 0;
            standby.play().catch(() => {});
        }

        rafRef.current = requestAnimationFrame(draw);
    }, [getVideos]);

    useEffect(() => {
        const a = videoA.current;
        const b = videoB.current;
        if (!a || !b) return;

        // Load both videos with the same src
        [a, b].forEach(v => {
            v.src = src;
            v.load();
        });

        // Start when A is ready
        const onReady = () => {
            a.play().catch(() => {});
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(draw);
        };
        a.addEventListener('loadeddata', onReady, { once: true });

        // When the active video ends, swap buffers
        const onEndedA = () => {
            if (activeRef.current === 'a') {
                activeRef.current = 'b';
                // Reset A so it can be the next standby
                a.currentTime = 0;
                a.pause();
            }
        };
        const onEndedB = () => {
            if (activeRef.current === 'b') {
                activeRef.current = 'a';
                b.currentTime = 0;
                b.pause();
                // A pre-roll should have already started; if not, start it now
                if (a.paused) { a.currentTime = 0; a.play().catch(() => {}); }
            }
        };

        a.addEventListener('ended', onEndedA);
        b.addEventListener('ended', onEndedB);

        return () => {
            a.removeEventListener('loadeddata', onReady);
            a.removeEventListener('ended', onEndedA);
            b.removeEventListener('ended', onEndedB);
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            a.pause();
            b.pause();
        };
    }, [src, draw]);

    const offscreenStyle: React.CSSProperties = {
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        width: '1px',
        height: '1px',
        pointerEvents: 'none',
    };

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...style }}
        >
            {/* Buffer A — off-screen, still decoded by the browser */}
            <video ref={videoA} muted playsInline preload="auto" style={offscreenStyle} />
            {/* Buffer B — standby, pre-rolled before A ends */}
            <video ref={videoB} muted playsInline preload="auto" style={offscreenStyle} />

            {/* Canvas — receives blitted tile frames every rAF tick */}
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
};

export default TiledVideoBackground;
